// src/driver/mixer.ts
var channelNotFound = (name) => `Channel "${name}" not found`;
var fadeIn = (gainNode, targetVolume, startTime, fadeInMs) => {
  gainNode.gain.setValueAtTime(0, startTime);
  gainNode.gain.linearRampToValueAtTime(targetVolume, startTime + fadeInMs / 1000);
};
var fadeOut = (gainNode, currentVolume, startTime, fadeOutMs) => {
  gainNode.gain.cancelScheduledValues(startTime);
  gainNode.gain.setValueAtTime(currentVolume, startTime);
  gainNode.gain.linearRampToValueAtTime(0, startTime + fadeOutMs / 1000);
};

class Mixer {
  _name;
  channels;
  _masterVolume;
  constructor(name) {
    this._name = name;
    this.channels = new Map;
    this._masterVolume = 1;
  }
  get name() {
    return this._name;
  }
  get masterVolume() {
    return this._masterVolume;
  }
  changeMasterVolume(volume) {
    this._masterVolume = volume;
    this.channels.forEach((channel) => {
      channel.gainNode.gain.setValueAtTime(channel.volume * this._masterVolume, 0);
    });
  }
  async addChannel(name, source, volume = 1, isLoop = false, loopStart = 0, loopEnd = 0) {
    const channel = this.channels.get(name);
    if (channel !== undefined) {
      channel.sourceNode.stop();
      channel.sourceNode.disconnect();
      channel.gainNode.disconnect();
      channel.ctx.close();
    }
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContextClass;
    const sourceNode = ctx.createBufferSource();
    const gainNode = ctx.createGain();
    const decodedData = await ctx.decodeAudioData(source);
    const sampleRate = decodedData.sampleRate;
    sourceNode.buffer = decodedData;
    sourceNode.loop = isLoop;
    sourceNode.loopStart = loopStart / sampleRate;
    sourceNode.loopEnd = loopEnd / sampleRate;
    sourceNode.connect(gainNode);
    gainNode.connect(ctx.destination);
    this.channels.set(name, {
      ctx,
      sourceNode,
      gainNode,
      playStatus: "Standby",
      volume
    });
  }
  removeChannel(name) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    channel.sourceNode.stop();
    channel.sourceNode.disconnect();
    channel.gainNode.disconnect();
    channel.ctx.close();
    this.channels.delete(name);
    return "";
  }
  playChannel(name, delayMs = 0, offsetMs = 0, fadeInMs = 0, fadeOutMs = 0) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    const { sourceNode, gainNode, volume } = channel;
    try {
      sourceNode.start(delayMs / 1000, offsetMs / 1000);
    } catch (error) {
      if (error instanceof Error)
        return error.message;
      return String(error);
    }
    fadeIn(gainNode, volume * this._masterVolume, 0, fadeInMs);
    if (!sourceNode.loop && sourceNode.buffer !== null) {
      const duration = sourceNode.buffer.duration;
      fadeOut(gainNode, volume * this._masterVolume, duration - fadeOutMs / 1000, fadeOutMs);
    }
    return "";
  }
  stopChannel(name, fadeOutMs = 0) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    const {
      ctx: { currentTime },
      sourceNode,
      gainNode,
      volume
    } = channel;
    fadeOut(gainNode, volume * this._masterVolume, currentTime, fadeOutMs);
    setTimeout(() => sourceNode.stop(), fadeOutMs);
    return "";
  }
  pauseChannel(name, fadeOutMs = 0) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    const { ctx, gainNode, volume } = channel;
    const { currentTime } = ctx;
    fadeOut(gainNode, volume * this._masterVolume, currentTime, fadeOutMs);
    setTimeout(() => ctx.suspend(), fadeOutMs);
    return "";
  }
  resumeChannel(name, fadeInMs = 0) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    const { ctx, gainNode, volume } = channel;
    const { currentTime } = ctx;
    ctx.resume();
    fadeIn(gainNode, volume * this._masterVolume, currentTime, fadeInMs);
    return "";
  }
  changeChannelVolume(name, volume) {
    const channel = this.channels.get(name);
    if (channel === undefined) {
      return channelNotFound(name);
    }
    const { gainNode } = channel;
    gainNode.gain.setValueAtTime(volume * this._masterVolume, 0);
    this.channels.set(name, { ...channel, volume });
    return "";
  }
}
// src/ui/widgets/custom-layout.ts
var customLayout = (props) => (children) => ({
  ...props,
  type: "CustomLayout",
  children
});
var isCustomLayout = (obj) => obj.type === "CustomLayout";
// src/ui/widgets/image.ts
var img = (props) => ({
  ...props,
  type: "Image"
});
// src/ui/widgets/layout.ts
var layout = (props) => (children) => ({
  ...props,
  type: "Layout",
  children
});
var isLayout = (obj) => obj.type === "Layout";
// src/ui/widgets/text.ts
var text = (props) => ({
  ...props,
  type: "Text"
});
// src/ui/widgets/text-box.ts
var textBox = (props) => (children) => ({
  ...props,
  type: "TextBox",
  children
});
var isTextBox = (obj) => obj.type === "TextBox";
// src/ui/manager.ts
class WidgetManager {
  _widgets;
  constructor(widgets = []) {
    this._widgets = widgets;
  }
  get widgets() {
    return Object.freeze([...this._widgets]);
  }
  addWidget(widget, layoutId) {
    if (widget.id !== undefined && this.hasId(widget.id)) {
      throw new Error(`Widget with id "${widget.id}" already exists`);
    }
    if (layoutId === undefined) {
      this._widgets.push(widget);
      return;
    }
    const added = this.addToLayout(widget, layoutId, this._widgets);
    if (!added) {
      throw new Error(`Layout with id "${layoutId}" not found`);
    }
  }
  addText(textWidget, textBoxId) {
    if (textWidget.id !== undefined && this.hasId(textWidget.id)) {
      throw new Error(`Widget with id "${textWidget.id}" already exists`);
    }
    const added = this.addToTextBox(textWidget, textBoxId, this._widgets);
    if (!added) {
      throw new Error(`TextBox with id "${textBoxId}" not found`);
    }
  }
  hasId(id) {
    return this.hasIdInWidgets(id, this._widgets);
  }
  hasIdInWidgets(id, widgets) {
    for (const obj of widgets) {
      if (obj.id === id) {
        return true;
      }
      if (isLayout(obj) || isCustomLayout(obj)) {
        if (this.hasIdInWidgets(id, obj.children)) {
          return true;
        }
      }
      if (isTextBox(obj)) {
        if (this.hasIdInWidgets(id, obj.children)) {
          return true;
        }
      }
    }
    return false;
  }
  findById(id, widgets) {
    const searchWidgets = widgets ?? this._widgets;
    for (const obj of searchWidgets) {
      if (obj.id === id) {
        return obj;
      }
      if (isLayout(obj) || isCustomLayout(obj)) {
        const foundInChildren = this.findById(id, obj.children);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
      if (isTextBox(obj)) {
        const foundInChildren = this.findById(id, obj.children);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  }
  addToLayout(widget, layoutId, widgets) {
    for (const obj of widgets) {
      if (obj.id === layoutId) {
        if (isLayout(obj) || isCustomLayout(obj)) {
          obj.children.push(widget);
          return true;
        }
        return false;
      }
      if (isLayout(obj) || isCustomLayout(obj)) {
        if (this.addToLayout(widget, layoutId, obj.children)) {
          return true;
        }
      }
    }
    return false;
  }
  addToTextBox(textWidget, textBoxId, widgets) {
    for (const obj of widgets) {
      if (obj.id === textBoxId) {
        if (isTextBox(obj)) {
          obj.children.push(textWidget);
          return true;
        }
        return false;
      }
      if (isLayout(obj) || isCustomLayout(obj)) {
        if (this.addToTextBox(textWidget, textBoxId, obj.children)) {
          return true;
        }
      }
    }
    return false;
  }
  clearTextBox(textBoxId) {
    const textBox2 = this.findById(textBoxId);
    if (!textBox2 || !isTextBox(textBox2)) {
      throw new Error(`TextBox with id "${textBoxId}" not found`);
    }
    textBox2.children = [];
  }
  removeById(id) {
    if (!this.hasId(id)) {
      throw new Error(`Widget with id "${id}" not found`);
    }
    const newWidgets = this.removeByIdFromWidgets(id, this._widgets);
    this._widgets = newWidgets;
  }
  removeByIdFromWidgets(id, widgets) {
    const result = [];
    for (const widget of widgets) {
      if (widget.id === id) {
        continue;
      }
      if (isLayout(widget) || isCustomLayout(widget) || isTextBox(widget)) {
        const newChildren = this.removeByIdFromWidgets(id, widget.children);
        widget.children = newChildren;
      }
      result.push(widget);
    }
    return result;
  }
}
// src/model.ts
var generateInitModel = (mixer2) => ({
  mixer: mixer2,
  ui: new WidgetManager,
  isDelaying: false,
  isFetching: false,
  error: null
});
// src/update/message-handlers/add-channel.ts
var addChannel = (name, src, volume, loop) => {
  return {
    type: "AddChannel",
    name,
    src,
    ...volume !== undefined ? { volume } : {},
    ...loop !== undefined ? { loop } : {}
  };
};
var handleAddChannel = (model, msg) => {
  return [
    { ...model, isFetching: true },
    async () => {
      try {
        const response = await fetch(msg.src);
        if (!response.ok) {
          throw new Error(`Failed to fetch audio from ${msg.src}: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        return {
          type: "SuccessFetchAudio",
          name: msg.name,
          ...msg.volume !== undefined ? { volume: msg.volume } : {},
          ...msg.loop !== undefined ? { loop: msg.loop } : {},
          arrayBuffer
        };
      } catch (error) {
        return {
          type: "Error",
          value: error
        };
      }
    }
  ];
};
var handleSuccessFetchAudio = (model, msg) => {
  model.mixer.addChannel(msg.name, msg.arrayBuffer, msg.volume ?? 1, msg.loop !== undefined, msg.loop?.start, msg.loop?.end);
  model.isFetching = false;
  return model;
};
// src/update/message-handlers/add-custom-layout.ts
var addCustomLayout = (id, component, parentLayoutId) => {
  return {
    type: "AddCustomLayout",
    id,
    component,
    ...parentLayoutId !== undefined ? { parentLayoutId } : {}
  };
};
var handleAddCustomLayout = (model, msg) => {
  const newLayout = customLayout({
    id: msg.id,
    component: msg.component
  })([]);
  model.ui.addWidget(newLayout, msg.parentLayoutId);
  return model;
};
// src/update/message-handlers/add-layout.ts
var addLayout = (id, parentLayoutId, style) => {
  return {
    type: "AddLayout",
    id,
    ...parentLayoutId !== undefined ? { parentLayoutId } : {},
    ...style !== undefined ? { style } : {}
  };
};
var handleAddLayout = (model, msg) => {
  const newLayout = layout({
    id: msg.id,
    ...msg.style !== undefined && { style: msg.style }
  })([]);
  model.ui.addWidget(newLayout, msg.parentLayoutId);
  return model;
};
// src/update/message-handlers/add-text-box.ts
var addTextBox = (id, layoutId, style) => {
  return {
    type: "AddTextBox",
    id,
    layoutId,
    ...style !== undefined ? { style } : {}
  };
};
var handleAddTextBox = (model, msg) => {
  const newTextBox = textBox({
    id: msg.id,
    ...msg.style !== undefined && { style: msg.style }
  })([]);
  model.ui.addWidget(newTextBox, msg.layoutId);
  return model;
};
// src/update/message-handlers/change-channel-volume.ts
var changeChannelVolume = (channelName, volume) => {
  return {
    type: "ChangeChannelVolume",
    channelName,
    volume
  };
};
var handleChangeChannelVolume = (model, msg) => {
  model.mixer.changeChannelVolume(msg.channelName, msg.volume);
  return model;
};
// src/update/message-handlers/change-master-volume.ts
var changeMasterVolume = (name, masterVolume) => {
  return {
    type: "ChangeMasterVolume",
    name,
    masterVolume
  };
};
var handleChangeMasterVolume = (model, msg) => {
  model.mixer.changeMasterVolume(msg.masterVolume);
  return model;
};
// src/update/message-handlers/clear-text-box.ts
var clearTextBox = (textBoxId) => {
  return {
    type: "ClearTextBox",
    textBoxId
  };
};
var handleClearTextBox = (model, msg) => {
  model.ui.clearTextBox(msg.textBoxId);
  return model;
};
// src/update/message-handlers/delay.ts
var delay = (durationMs) => {
  return {
    type: "Delay",
    durationMs
  };
};
var handleDelay = (model, msg) => {
  model.isDelaying = true;
  return [
    model,
    async () => await new Promise((resolve) => {
      setTimeout(() => {
        resolve({ type: "DelayCompleted" });
      }, msg.durationMs);
    })
  ];
};
var handleDelayCompleted = (model, _msg) => {
  model.isDelaying = false;
  return model;
};
// src/update/message-handlers/error.ts
var error = (value) => {
  return {
    type: "Error",
    value
  };
};
var handleError = (model, msg) => {
  return {
    ...model,
    error: msg.value
  };
};
var handleRecoverError = (model) => {
  return {
    ...model,
    error: null
  };
};
// src/update/message-handlers/pause-channel.ts
var pauseChannel = (channelName, fadeOutMs) => {
  return {
    type: "PauseChannel",
    channelName,
    ...fadeOutMs !== undefined ? { fadeOutMs } : {}
  };
};
var handlePauseChannel = (model, msg) => {
  model.mixer.pauseChannel(msg.channelName, msg.fadeOutMs);
  return model;
};
// src/update/message-handlers/play-channel.ts
var playChannel = (channelName, delayMs, offsetMs, fadeInMs, fadeOutMs) => {
  return {
    type: "PlayChannel",
    channelName,
    ...delayMs !== undefined ? { delayMs } : {},
    ...offsetMs !== undefined ? { offsetMs } : {},
    ...fadeInMs !== undefined ? { fadeInMs } : {},
    ...fadeOutMs !== undefined ? { fadeOutMs } : {}
  };
};
var handlePlayChannel = (model, msg) => {
  model.mixer.playChannel(msg.channelName, msg.delayMs, msg.offsetMs, msg.fadeInMs, msg.fadeOutMs);
  return model;
};
// src/update/message-handlers/remove-channel.ts
var removeChannel = (name) => {
  return {
    type: "RemoveChannel",
    name
  };
};
var handleRemoveChannel = (model, msg) => {
  model.mixer.removeChannel(msg.name);
  return model;
};
// src/update/message-handlers/remove-widgets.ts
var removeWidgets = (ids) => {
  return {
    type: "RemoveWidgets",
    ids
  };
};
var handleRemoveWidgets = (model, msg) => {
  for (const id of msg.ids) {
    model.ui.removeById(id);
  }
  return model;
};
// src/update/message-handlers/resume-channel.ts
var resumeChannel = (channelName, fadeInMs) => {
  return {
    type: "ResumeChannel",
    channelName,
    ...fadeInMs !== undefined ? { fadeInMs } : {}
  };
};
var handleResumeChannel = (model, msg) => {
  model.mixer.resumeChannel(msg.channelName, msg.fadeInMs);
  return model;
};
// src/update/message-handlers/sequence.ts
var sequence = (messages) => {
  return {
    type: "Sequence",
    messages
  };
};
var handleSequence = (model, msg, update) => {
  if (msg.messages.length === 0) {
    return model;
  }
  const messages = [];
  const restMessages = [];
  const delayIndex = msg.messages.findIndex((m) => m.type === "Delay");
  if (delayIndex !== -1) {
    messages.push(...msg.messages.slice(0, delayIndex + 1));
    restMessages.push(...msg.messages.slice(delayIndex + 1));
  } else {
    messages.push(...msg.messages);
  }
  const result = messages.reduce((acc, cur) => {
    const updateResult = update(acc.model, cur);
    const [nextModel, nextCmd] = Array.isArray(updateResult) ? updateResult : [updateResult, undefined];
    return {
      model: nextModel,
      cmds: [...acc.cmds, ...nextCmd ? [nextCmd] : []]
    };
  }, { model, cmds: [] });
  return [
    result.model,
    async () => {
      const cmdMsgs = await Promise.all(result.cmds.map((cmd) => cmd()));
      const msg2 = {
        type: "Sequence",
        messages: [...restMessages, ...cmdMsgs]
      };
      return msg2;
    }
  ];
};
// src/update/message-handlers/show-image.ts
var showImage = (layoutId, src, id, style) => {
  return {
    type: "ShowImage",
    layoutId,
    src,
    ...id !== undefined ? { id } : {},
    ...style !== undefined ? { style } : {}
  };
};
var handleShowImage = (model, msg) => {
  const newImg = img({
    src: msg.src,
    ...msg.id !== undefined && { id: msg.id },
    ...msg.style !== undefined && { style: msg.style }
  });
  model.ui.addWidget(newImg, msg.layoutId);
  return model;
};
// src/update/message-handlers/show-text.ts
var showText = (textBoxId, content, id, style, speed) => {
  return {
    type: "ShowText",
    textBoxId,
    content,
    ...id !== undefined ? { id } : {},
    ...style !== undefined ? { style } : {},
    ...speed !== undefined ? { speed } : {}
  };
};
var handleShowText = (model, msg) => {
  const newText = text({
    content: msg.content,
    ...msg.id !== undefined && { id: msg.id },
    ...msg.style !== undefined && { style: msg.style },
    ...msg.speed !== undefined && { speed: msg.speed }
  });
  model.ui.addText(newText, msg.textBoxId);
  return model;
};
// src/update/message-handlers/stop-channel.ts
var stopChannel = (channelName, fadeOutMs) => {
  return {
    type: "StopChannel",
    channelName,
    ...fadeOutMs !== undefined ? { fadeOutMs } : {}
  };
};
var handleStopChannel = (model, msg) => {
  model.mixer.stopChannel(msg.channelName, msg.fadeOutMs);
  return model;
};
// src/update/update.ts
var update = (model, msg) => {
  switch (msg.type) {
    case "AddLayout":
      return handleAddLayout(model, msg);
    case "AddCustomLayout":
      return handleAddCustomLayout(model, msg);
    case "ShowImage":
      return handleShowImage(model, msg);
    case "AddTextBox":
      return handleAddTextBox(model, msg);
    case "ShowText":
      return handleShowText(model, msg);
    case "RemoveWidgets":
      return handleRemoveWidgets(model, msg);
    case "Delay":
      return handleDelay(model, msg);
    case "DelayCompleted":
      return handleDelayCompleted(model, msg);
    case "ChangeMasterVolume":
      return handleChangeMasterVolume(model, msg);
    case "ClearTextBox":
      return handleClearTextBox(model, msg);
    case "AddChannel":
      return handleAddChannel(model, msg);
    case "SuccessFetchAudio":
      return handleSuccessFetchAudio(model, msg);
    case "RemoveChannel":
      return handleRemoveChannel(model, msg);
    case "ChangeChannelVolume":
      return handleChangeChannelVolume(model, msg);
    case "PlayChannel":
      return handlePlayChannel(model, msg);
    case "StopChannel":
      return handleStopChannel(model, msg);
    case "PauseChannel":
      return handlePauseChannel(model, msg);
    case "ResumeChannel":
      return handleResumeChannel(model, msg);
    case "Error":
      return handleError(model, msg);
    case "RecoverError":
      return handleRecoverError(model);
    case "Sequence":
      return handleSequence(model, msg, update);
    default: {
      const error3 = new Error(`Unhandled message type: ${JSON.stringify(msg)}`);
      return handleError(model, { type: "Error", value: error3 });
    }
  }
};
export {
  update,
  textBox,
  text,
  stopChannel,
  showText,
  showImage,
  sequence,
  resumeChannel,
  removeWidgets,
  removeChannel,
  playChannel,
  pauseChannel,
  layout,
  isTextBox,
  isLayout,
  isCustomLayout,
  img,
  handleSuccessFetchAudio,
  handleStopChannel,
  handleShowText,
  handleShowImage,
  handleSequence,
  handleResumeChannel,
  handleRemoveWidgets,
  handleRemoveChannel,
  handleRecoverError,
  handlePlayChannel,
  handlePauseChannel,
  handleError,
  handleDelayCompleted,
  handleDelay,
  handleClearTextBox,
  handleChangeMasterVolume,
  handleChangeChannelVolume,
  handleAddTextBox,
  handleAddLayout,
  handleAddCustomLayout,
  handleAddChannel,
  generateInitModel,
  error,
  delay,
  customLayout,
  clearTextBox,
  changeMasterVolume,
  changeChannelVolume,
  addTextBox,
  addLayout,
  addCustomLayout,
  addChannel,
  WidgetManager,
  Mixer
};

//# debugId=75C3CD495C8C50E964756E2164756E21
//# sourceMappingURL=index.js.map
